import React, { useEffect, useState, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { doc, getDoc, collection, getDocs, query, where, updateDoc, addDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../config/fbConfig';
import Loader from '../components/Loader';
import Logo from '../images/school_logo.png';
import '../styles/ClassDetailsPage.css';
import Modal from 'react-modal';
import { useUserAuth } from "../context/UserAuthContext";
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import autoTable from 'jspdf-autotable';
import SuccessModal from './SuccessModal';
import { SCHOOL_ACRONYM, SCHOOL_NAME } from '../Variables.js';
import generateReceiptPDF from './PaymentReceiptGenerator';

Modal.setAppElement('#root');

const Fees = () => {
  const { classId } = useParams();
  const { user, userData } = useUserAuth();
  const [loading, setLoading] = useState(true);
  const [classInfo, setClassInfo] = useState(null);
  const [students, setStudents] = useState([]);
  const [showFeesModal, setShowFeesModal] = useState(false);
  const [feesAmount, setFeesAmount] = useState(0);
  const [isEditStudentFeesModalOpen, setIsEditStudentFeesModalOpen] = useState(false);
  const [isPayStudentFeesModalOpen, setIsPayStudentFeesModalOpen] = useState(false);
  const [currentStudentFees, setCurrentStudentFees] = useState(null);
  const [paidAmount, setPaidAmount] = useState(0);
  const [payerName, setPayerName] = useState('');
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [fetchingHistory, setFetchingHistory] = useState(false);
  const [paymentFor, setPaymentFor] = useState(['School fees']);
  const [paymentMethod, setPaymentMethod] = useState('Transfer');
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [paymentToDelete, setPaymentToDelete] = useState(null);
  const paymentForOptions = [
    'School fees',
    'Outstanding balance',
    'Stationery',
    'Uniform',
    'Sportswear',
    'Cardigan',
    'Charges',
    'Others'
  ];
  const paymentMethodOptions = [    
    'Cash',
    'Transfer-Sterling',
    'Transfer-Zenith',
    'Transfer-UBA',
    'Transfer-POS',
    'POS',
  ];
  const navigate = useNavigate();

  const fetchData = async () => {
    setError("");
    setSuccessMessage("");
    setShowModal(false);
    setLoading(true);
    try {
      const classRef = doc(db, "classes", classId);
      const classSnap = await getDoc(classRef);

      if (!classSnap.exists()) {
        setError(`Class with ID "${classId}" not found in Firestore`);
        setShowModal(true);
        setLoading(false);
        return;
      }

      const classData = classSnap.data();

      const [sectionDoc, sessionDoc, termDoc] = await Promise.all([
        classData.section ? getDoc(doc(db, "sections", classData.section)) : null,
        classData.session ? getDoc(doc(db, "sessions", classData.session)) : null,
        classData.term ? getDoc(doc(db, "terms", classData.term)) : null,
      ]);

      const sectionName = sectionDoc?.exists() ? sectionDoc.data().name : "Unknown section";
      const sessionName = sessionDoc?.exists() ? sessionDoc.data().name : "Unknown session";
      const termName = termDoc?.exists() ? termDoc.data().name : "Unknown term";

      const studentsRef = collection(db, "students");
      const allStudentsSnap = await getDocs(studentsRef);
      const allStudents = allStudentsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const filteredStudents = allStudents.filter(student => classData.students?.includes(student.id));

      setStudents(filteredStudents);

      setClassInfo({
        ...classData,
        section: sectionName,
        session: sessionName,
        term: termName,
      });
    } catch (error) {
      setError(`Error fetching class data: ${error.message}`);
      setShowModal(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!classId) {
      setError("No class ID provided");
      setShowModal(true);
      setLoading(false);
      return;
    }
    fetchData();
  }, [classId]);

  const getEffectiveFeesAndBalance = (student) => {
    const enrollment = student.enrollments?.find(e => e.classId === classId) || {
      fees: 0,
      paid: 0
    };

    const base = Number(enrollment.fees) || 0;
    const paid = Number(enrollment.paid) || 0;

    const status   = (student.status || "Regular").trim();
    const discount = Number(student.discountAmount) || 0;

    let effective = base;

    if (status === "Scholarship") {
      effective = 0;
    } else if (status === "Discount") {
      effective = Math.max(0, base - discount);
    }

    const balance = Math.max(0, effective - paid);

    return { effective, balance, paid, status, discount };
  };

  const financialTotals = useMemo(() => {
    let expectedIncome = 0;
    let actualIncome = 0;
    let outstandingBalance = 0;

    students.forEach(student => {
      const { effective, paid, balance } = getEffectiveFeesAndBalance(student);

      expectedIncome     += effective;
      actualIncome       += paid;
      outstandingBalance += balance;
    });

    return { expectedIncome, actualIncome, outstandingBalance };
  }, [students, classId]);

  const generateFeesReportPDF = () => {
  // Categorize students by status
  const regularStudents = students.filter(student => (student.status || "Regular") === "Regular");
  const discountStudents = students.filter(student => (student.status || "Regular") === "Discount");
  const scholarshipStudents = students.filter(student => (student.status || "Regular") === "Scholarship");

  if (students.length === 0) {
    setError("No students enrolled in this class");
    setShowModal(true);
    return;
  }

  const doc = new jsPDF();
  const currentDate = new Date().toLocaleString();
  let pageCount = 1;

  // Helper function to check and add new page
  const checkAndAddPage = (startY, requiredSpace = 40) => {
    if (startY > doc.internal.pageSize.getHeight() - requiredSpace) {
      doc.addPage();
      pageCount++;
      return 20; // Reset Y position for new page
    }
    return startY;
  };

  // Helper function to get student financial data with status consideration
  const getStudentFinancials = (student) => {
    const enrollment = student.enrollments?.find(e => e.classId === classId) || {
      fees: 0,
      paid: 0
    };
    
    const baseFees = Number(enrollment.fees) || 0;
    const paid = Number(enrollment.paid) || 0;
    const status = student.status || "Regular";
    const discount = Number(student.discountAmount) || 0;
    
    let effectiveFees = baseFees;
    if (status === "Scholarship") {
      effectiveFees = 0;
    } else if (status === "Discount") {
      effectiveFees = Math.max(0, baseFees - discount);
    }
    
    const balance = Math.max(0, effectiveFees - paid);
    
    return { baseFees, effectiveFees, paid, balance, status, discount };
  };

  // Set document header
  const setHeader = (title) => {
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text(`${SCHOOL_ACRONYM} Fees Report`, 105, 15, { align: 'center' });
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text(`Class: ${classInfo.name} (${classInfo.session}-${classInfo.term} Term)`, 105, 20, { align: 'center' });
    doc.text(`Generated on: ${currentDate}`, 105, 25, { align: 'center' });
    doc.text(`Cashier: ${userData.firstName} ${userData.lastName}`, 105, 30, { align: 'center' });
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text(title, 14, 35);
    return 40;
  };

  let startY = 5;

  // ==================== REGULAR STUDENTS SECTION ====================
  if (regularStudents.length > 0) {
    // Separate regular students by payment status
    const regularOutstanding = regularStudents.filter(student => {
      const { balance } = getStudentFinancials(student);
      return balance > 0;
    });
    
    const regularFullyPaid = regularStudents.filter(student => {
      const { effectiveFees, paid } = getStudentFinancials(student);
      return effectiveFees > 0 && paid >= effectiveFees;
    });

    // Regular Students Header
    startY = setHeader("REGULAR STUDENTS");

    // Outstanding Regular Students Table
    if (regularOutstanding.length > 0) {
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('Students with Outstanding Balance', 14, startY);
      startY += 6;

      let totalFees = 0, totalPaid = 0, totalBalance = 0;
      const outstandingData = regularOutstanding.map((student, index) => {
        const { baseFees, effectiveFees, paid, balance } = getStudentFinancials(student);
        totalFees += effectiveFees;
        totalPaid += paid;
        totalBalance += balance;
        
        return [
          index + 1,
          `${student.firstName} ${student.lastName}`,
          student.fathersName || 'N/A',
          student.fathersPhone1 || 'N/A',
          effectiveFees.toLocaleString(),
          paid.toLocaleString(),
          balance.toLocaleString()
        ];
      });

      outstandingData.push([
        '',
        'TOTAL',
        '',
        '',
        totalFees.toLocaleString(),
        totalPaid.toLocaleString(),
        totalBalance.toLocaleString()
      ]);

      autoTable(doc, {
        head: [['S/N', 'Student Name', "Father's Name", "Father's Phone", 'Fees (N)', 'Paid (N)', 'Balance (N)']],
        body: outstandingData,
        startY: startY,
        styles: {
          cellPadding: 2,
          fontSize: 9,
          valign: 'middle',
          lineWidth: 0.1,
          lineColor: [0, 0, 0],
          textColor: 0,
        },
        headStyles: {
          fillColor: [41, 128, 185],
          textColor: 255,
          fontStyle: 'bold'
        },
        columnStyles: {
          0: { cellWidth: 10 },
          1: { cellWidth: 45 },
          2: { cellWidth: 40 },
          3: { cellWidth: 30 },
          4: { cellWidth: 20 },
          5: { cellWidth: 20 },
          6: { cellWidth: 20, textColor: [255, 0, 0] }
        }
      });

      startY = doc.lastAutoTable.finalY + 10;
      startY = checkAndAddPage(startY);
    }

    // Fully Paid Regular Students Table
    if (regularFullyPaid.length > 0) {
      startY = checkAndAddPage(startY);
      
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('Students with Fully Paid Fees', 14, startY);
      startY += 6;

      let totalFees = 0, totalPaid = 0;
      const fullyPaidData = regularFullyPaid.map((student, index) => {
        const { effectiveFees, paid } = getStudentFinancials(student);
        totalFees += effectiveFees;
        totalPaid += paid;
        
        return [
          index + 1,
          `${student.firstName} ${student.lastName}`,
          student.fathersName || 'N/A',
          student.fathersPhone1 || 'N/A',
          effectiveFees.toLocaleString(),
          paid.toLocaleString()
        ];
      });

      fullyPaidData.push([
        '',
        'TOTAL',
        '',
        '',
        totalFees.toLocaleString(),
        totalPaid.toLocaleString()
      ]);

      autoTable(doc, {
        head: [['S/N', 'Student Name', "Father's Name", "Father's Phone", 'Fees (N)', 'Paid (N)']],
        body: fullyPaidData,
        startY: startY,
        styles: {
          cellPadding: 2,
          fontSize: 9,
          valign: 'middle',
          lineWidth: 0.1,
          lineColor: [0, 0, 0],
          textColor: 0,
        },
        headStyles: {
          fillColor: [39, 174, 96],
          textColor: 255,
          fontStyle: 'bold'
        }
      });

      startY = doc.lastAutoTable.finalY + 15;
    } else if (regularOutstanding.length === 0) {
      doc.setFontSize(12);
      doc.setFont('helvetica', 'italic');
      doc.text('No regular students found', 14, startY);
      startY += 15;
    }
  }

  // ==================== DISCOUNT STUDENTS SECTION ====================
  if (discountStudents.length > 0) {
    // Add new page for discount students
    doc.addPage();
    pageCount++;
    startY = 20;

    // Separate discount students by payment status
    const discountOutstanding = discountStudents.filter(student => {
      const { balance } = getStudentFinancials(student);
      return balance > 0;
    });
    
    const discountFullyPaid = discountStudents.filter(student => {
      const { effectiveFees, paid } = getStudentFinancials(student);
      return effectiveFees > 0 && paid >= effectiveFees;
    });

    // Discount Students Header
    startY = setHeader("DISCOUNT STUDENTS");

    // Outstanding Discount Students Table
    if (discountOutstanding.length > 0) {
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('Students with Outstanding Balance', 14, startY);
      startY += 6;

      let totalFees = 0, totalPaid = 0, totalBalance = 0;
      const outstandingData = discountOutstanding.map((student, index) => {
        const { baseFees, effectiveFees, paid, balance, discount } = getStudentFinancials(student);
        totalFees += effectiveFees;
        totalPaid += paid;
        totalBalance += balance;
        
        return [
          index + 1,
          `${student.firstName} ${student.lastName}`,
          discount.toLocaleString(),
          effectiveFees.toLocaleString(),
          paid.toLocaleString(),
          balance.toLocaleString()
        ];
      });

      outstandingData.push([
        '',
        'TOTAL',
        '',
        totalFees.toLocaleString(),
        totalPaid.toLocaleString(),
        totalBalance.toLocaleString()
      ]);

      autoTable(doc, {
        head: [['S/N', 'Student Name', 'Discount (N)', 'Fees After Discount (N)', 'Paid (N)', 'Balance (N)']],
        body: outstandingData,
        startY: startY,
        styles: {
          cellPadding: 2,
          fontSize: 9,
          valign: 'middle',
          lineWidth: 0.1,
          lineColor: [0, 0, 0],
          textColor: 0,
        },
        headStyles: {
          fillColor: [230, 126, 34],
          textColor: 255,
          fontStyle: 'bold'
        },
        columnStyles: {
          0: { cellWidth: 10 },
          1: { cellWidth: 50 },
          2: { cellWidth: 25 },
          3: { cellWidth: 30 },
          4: { cellWidth: 25 },
          5: { cellWidth: 25, textColor: [255, 0, 0] }
        }
      });

      startY = doc.lastAutoTable.finalY + 10;
      startY = checkAndAddPage(startY);
    }

    // Fully Paid Discount Students Table
    if (discountFullyPaid.length > 0) {
      startY = checkAndAddPage(startY);
      
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('Students with Fully Paid Fees', 14, startY);
      startY += 6;

      let totalFees = 0, totalPaid = 0;
      const fullyPaidData = discountFullyPaid.map((student, index) => {
        const { baseFees, effectiveFees, paid, discount } = getStudentFinancials(student);
        totalFees += effectiveFees;
        totalPaid += paid;
        
        return [
          index + 1,
          `${student.firstName} ${student.lastName}`,
          discount.toLocaleString(),
          effectiveFees.toLocaleString(),
          paid.toLocaleString()
        ];
      });

      fullyPaidData.push([
        '',
        'TOTAL',
        '',
        totalFees.toLocaleString(),
        totalPaid.toLocaleString()
      ]);

      autoTable(doc, {
        head: [['S/N', 'Student Name', 'Discount (N)', 'Fees After Discount (N)', 'Paid (N)']],
        body: fullyPaidData,
        startY: startY,
        styles: {
          cellPadding: 2,
          fontSize: 9,
          valign: 'middle',
          lineWidth: 0.1,
          lineColor: [0, 0, 0],
          textColor: 0,
        },
        headStyles: {
          fillColor: [39, 174, 96],
          textColor: 255,
          fontStyle: 'bold'
        }
      });

      startY = doc.lastAutoTable.finalY + 15;
    } else if (discountOutstanding.length === 0) {
      doc.setFontSize(12);
      doc.setFont('helvetica', 'italic');
      doc.text('No discount students found', 14, startY);
      startY += 15;
    }
  }

  // ==================== SCHOLARSHIP STUDENTS SECTION ====================
  if (scholarshipStudents.length > 0) {
    // Add new page for scholarship students
    doc.addPage();
    pageCount++;
    startY = 20;

    // Scholarship Students Header (only one table since balance is always 0)
    startY = setHeader("SCHOLARSHIP STUDENTS");

    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('All Scholarship Students (Fees: N0)', 14, startY);
    startY += 6;

    const scholarshipData = scholarshipStudents.map((student, index) => {
      const { paid } = getStudentFinancials(student);
      return [
        index + 1,
        `${student.firstName} ${student.lastName}`,
        student.fathersName || 'N/A',
        student.fathersPhone1 || 'N/A',
        'N0',
        paid.toLocaleString(),
        'N0'
      ];
    });

    let totalPaid = scholarshipStudents.reduce((sum, student) => {
      const { paid } = getStudentFinancials(student);
      return sum + paid;
    }, 0);

    scholarshipData.push([
      '',
      'TOTAL',
      '',
      '',
      'N0',
      totalPaid.toLocaleString(),
      'N0'
    ]);

    autoTable(doc, {
      head: [['S/N', 'Student Name', "Father's Name", "Father's Phone", 'Fees (N)', 'Paid (N)', 'Balance (N)']],
      body: scholarshipData,
      startY: startY,
      styles: {
        cellPadding: 2,
        fontSize: 9,
        valign: 'middle',
        lineWidth: 0.1,
        lineColor: [0, 0, 0],
        textColor: 0,
      },
      headStyles: {
        fillColor: [0, 96, 100],
        textColor: 255,
        fontStyle: 'bold'
      },
      columnStyles: {
        4: { textColor: [0, 128, 0] },
        5: { textColor: [0, 0, 0] },
        6: { textColor: [0, 128, 0] }
      }
    });

    startY = doc.lastAutoTable.finalY + 15;
  }

  // ==================== SUMMARY SECTION ====================
  // Check if we need a new page for summary
  if (startY > doc.internal.pageSize.getHeight() - 40) {
    doc.addPage();
    pageCount++;
    startY = 20;
  } else {
    startY += 5;
  }

  // Calculate totals by status
  let regularExpected = 0, regularActual = 0, regularOutstanding = 0;
  let discountExpected = 0, discountActual = 0, discountOutstanding = 0;
  let scholarshipExpected = 0, scholarshipActual = 0;

  students.forEach(student => {
    const { effectiveFees, paid, balance, status } = getStudentFinancials(student);
    
    if (status === "Regular") {
      regularExpected += effectiveFees;
      regularActual += paid;
      regularOutstanding += balance;
    } else if (status === "Discount") {
      discountExpected += effectiveFees;
      discountActual += paid;
      discountOutstanding += balance;
    } else if (status === "Scholarship") {
      scholarshipExpected += effectiveFees;
      scholarshipActual += paid;
    }
  });

  // Summary Table
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('FINANCIAL SUMMARY BY STATUS', 105, startY, { align: 'center' });
  startY += 7;

  const summaryData = [
    ['Regular Students', 
      `N${regularExpected.toLocaleString()}`,
      `N${regularActual.toLocaleString()}`,
      `N${regularOutstanding.toLocaleString()}`
    ],
    ['Discount Students', 
      `N${discountExpected.toLocaleString()}`,
      `N${discountActual.toLocaleString()}`,
      `N${discountOutstanding.toLocaleString()}`
    ],
    ['Scholarship Students', 
      `N${scholarshipExpected.toLocaleString()}`,
      `N${scholarshipActual.toLocaleString()}`,
      'N0'
    ],
    ['GRAND TOTAL', 
      `N${(regularExpected + discountExpected + scholarshipExpected).toLocaleString()}`,
      `N${(regularActual + discountActual + scholarshipActual).toLocaleString()}`,
      `N${(regularOutstanding + discountOutstanding).toLocaleString()}`
    ]
  ];

  autoTable(doc, {
    head: [['Category', 'Expected Income (N)', 'Actual Income (N)', 'Outstanding (N)']],
    body: summaryData,
    startY: startY,
    styles: {
      cellPadding: 3,
      fontSize: 10,
      fontStyle: 'bold',
      halign: 'center',
      textColor: 0,
    },
    headStyles: {
      fillColor: [52, 73, 94],
      textColor: 255
    },
    columnStyles: {
      0: { halign: 'left', fontStyle: 'bold' }
    }
  });

  // Add footer with page numbers
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(128);
    doc.text(
      `Page ${i} of ${totalPages} - Generated by ${userData.firstName} ${userData.lastName}`,
      doc.internal.pageSize.getWidth() / 2,
      doc.internal.pageSize.getHeight() - 5,
      { align: 'center' }
    );
  }

  // Save the PDF
  doc.save(`Fees_Report_${classInfo.name}_${currentDate.replace(/[/:, ]/g, '_')}.pdf`);
};










  const generateStudentPassesPDF = () => {
    const studentsWithPass = students.filter(student => {
      const enrollment = student.enrollments?.find(e => e.classId === classId) || {
        fees: 0,
        paid: 0
      };
      const fees = Number(enrollment.fees) || 0;
      const paid = Number(enrollment.paid) || 0;
      return fees > 0 && (fees - paid) <= 0;
    });

    if (studentsWithPass.length === 0) {
      setError("No students with complete payments");
      setShowModal(true);
      return;
    }

    const doc = new jsPDF();
    const currentDate = new Date().toLocaleString();
    const pageWidth = doc.internal.pageSize.getWidth();

    let yPos = 20;
    let xPos = 15;
    let passesInRow = 0;

    const maxSchoolNameWidth = 60;
    const lineHeight = 5;

    studentsWithPass.forEach((student, index) => {
      if (yPos > doc.internal.pageSize.getHeight() - 50) {
        doc.addPage();
        yPos = 20;
        xPos = 15;
        passesInRow = 0;
      }

      if (passesInRow >= 2) {
        yPos += 50;
        xPos = 15;
        passesInRow = 0;
      }

      doc.rect(xPos, yPos, 85, 45);

      doc.setGState(new doc.GState({opacity: 0.2}));
      doc.addImage(Logo, 'PNG', xPos + 50, yPos + 10, 30, 30);
      doc.setGState(new doc.GState({opacity: 1}));

      doc.addImage(Logo, 'PNG', xPos + 5, yPos + 5, 15, 15);
      doc.setFontSize(10);

      const splitSchoolName = doc.splitTextToSize(SCHOOL_NAME, maxSchoolNameWidth);
      splitSchoolName.forEach((line, i) => {
        doc.text(line, xPos + 23, yPos + 8 + (i * lineHeight));
      });

      doc.setFont("helvetica", "bold");
      doc.setFontSize(18);

      const text = "Student Pass";
      const textWidth = doc.getStringUnitWidth(text) * doc.internal.getFontSize() / doc.internal.scaleFactor;
      const centerX = xPos + (85 / 2) - (textWidth / 2);

      doc.setTextColor(50, 50, 50);
      doc.text(text, centerX, yPos + 14 + (splitSchoolName.length - 1) * lineHeight);

      doc.setFont("helvetica", "normal");

      doc.setFontSize(10);
      doc.text(`Name: ${student.firstName} ${student.lastName}`, xPos + 5, yPos + 24);
      doc.text(`Class: ${classInfo.name}`, xPos + 5, yPos + 29);
      doc.text(`Adm No: ${student.admissionNumber}`, xPos + 5, yPos + 34);
      doc.text(`${classInfo.section} Section - ${classInfo.term} Term - ${classInfo.session}`, xPos + 5, yPos + 39);

      xPos += 90;
      passesInRow++;
    });

    doc.save(`Student_Passes_${classInfo.name}_${currentDate.replace(/[/:, ]/g, '_')}.pdf`);
  };

  const fetchPaymentHistory = async (student) => {
    try {
      setFetchingHistory(true);
      setPaymentHistory([]);
      const historyRef = collection(db, "paymentHistory");
      const q = query(historyRef, where("studentName", "==", `${student.firstName} ${student.lastName}`));
      const historySnap = await getDocs(q);
      const history = historySnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      history.sort((a, b) => b.timestamp.toDate() - a.timestamp.toDate());
      setPaymentHistory(history);
    } catch (error) {
      setError(`Error fetching payment history: ${error.message}`);
      setShowModal(true);
    } finally {
      setFetchingHistory(false);
    }
  };

  if (loading) return <Loader text="LOADING" />;
  if (!classInfo) return <p className='bold caps align-center justify-center'>{error || 'Class not found'}</p>;

  return (
    <div className="login-page-items column align-center">
      <div className="top-elements row align-center space-between">
        <button onClick={() => navigate(-1)} className="back-link back-link1">
          <i className="fa-solid fa-arrow-left size3"></i>
        </button>
        <div className="column align-center justify-center">
          <img src={Logo} className="middle-logo" alt="logo" />
        </div>
        <Link to="#" className="home-link" onClick={() => window.location.reload()}>
          <i className="fa-solid fa-rotate-right size4"></i>
        </Link>
      </div>

      <h2 className="manage-page-heading size4">Fees for {classInfo.name} ({classInfo.session}-{classInfo.term} Term)</h2>

      <div className="tab-section column justify-center align-center">
        <div className="tab-section-top row justify-center align-center gap-15">
          <button 
            className='tab-section-button tab-section-button-fees caps' 
            onClick={() => {
              setFeesAmount(0);
              setShowFeesModal(true);
            }}
          >
            Assign Fees
          </button>
          <button 
            className='tab-section-button tab-section-button-fees caps' 
            onClick={generateFeesReportPDF}
          >
            Print Report
          </button>
          <button 
            className='tab-section-button tab-section-button-fees caps' 
            onClick={generateStudentPassesPDF}
          >
            Print Pass
          </button>
        </div>

        {students.length === 0 ? (
          <p className='text-center male-female-stat'>No students enrolled</p>
        ) : (
          <div className="fees-table-container">
            <table className="fees-table">
              <thead>
                <tr>
                  <th style={{ width: "200px" }}>Student</th>
                  <th style={{ width: "150px" }}>Fees</th>
                  <th style={{ width: "150px" }}>Paid</th>
                  <th style={{ width: "150px" }}>Bal</th>
                  <th style={{ width: "150px" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {students.map(student => {
                  const { effective, paid, balance, status, discount } = getEffectiveFeesAndBalance(student);                 
                  return (
                    <tr key={student.id}>
                      <td style={{ width: "200px" }}>{student.firstName} {student.lastName}<br />
                        <small>({student.admissionNumber})</small><br />
                        {status !== "Regular" && (
                          <small style={{
                            color: status === "Scholarship" ? '#006064' : '#ef6c00',
                            fontSize: '0.8rem'
                          }}>
                            {status}
                            {status === "Discount" && discount > 0 && <> (-₦{discount.toLocaleString()})</>}
                          </small>
                        )}
                      </td>
                      <td style={{ width: "150px" }}>{effective.toLocaleString()}</td>
                      <td style={{ width: "150px" }}>{paid.toLocaleString()}</td>
                      <td style={{ width: "150px" }}>{balance.toLocaleString()}</td>
                      <td style={{ width: "150px" }}>
                        <div className='row gap-1'>
                          <button 
                            className="list-btn"
                            onClick={() => {
                              setCurrentStudentFees(student);
                              setFeesAmount(effective);
                              setIsEditStudentFeesModalOpen(true);
                            }}
                          >
                            <i className="fas fa-edit"></i>
                          </button>
                          <button 
                            className="list-btn"
                            onClick={() => {
                              setCurrentStudentFees(student);
                              setPaidAmount(0);
                              setPayerName('');
                              setPaymentFor(['School fees']);
                              setPaymentMethod('Cash');
                              setIsPayStudentFeesModalOpen(true);
                            }}
                          >
                            <i className="fas fa-money-bill"></i>
                          </button>
                          <button 
                            className="list-btn"
                            onClick={() => {
                              setCurrentStudentFees(student);
                              setShowHistoryModal(true);
                              fetchPaymentHistory(student);
                            }}
                          >
                            <i className="fas fa-history"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <SuccessModal
          icon={error ? "xmark" : "check"}
          iconColor={error ? "red" : "green"}
          message={error || successMessage}
          onClose={() => {
            setError("");
            setSuccessMessage("");
            setShowModal(false);
          }}
        />
      )}

      <Modal
        isOpen={showFeesModal}
        onRequestClose={() => setShowFeesModal(false)}
        contentLabel="Assign Fees to Class"
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
      >
        <div>
          <p className="size5 caps">Assign Fees to <strong>{classInfo.name}</strong></p>
          <div className="modal-form">
            <label className='text-left'>Enter Fees</label>
            <input
              type="number"
              className="input-field"
              placeholder="Fees Amount"
              value={feesAmount === 0 ? '' : feesAmount}
              onChange={(e) => setFeesAmount(e.target.value)}
            />
            <div className="row align-center justify-center gap-15">
              <button 
                className="login-button"
                onClick={() => setShowFeesModal(false)}
              >
                Cancel
              </button>
              <button 
                className="login-button"
                onClick={async () => {
                  try {
                    setLoading(true);
                    await Promise.all(
                      students.map(async (student) => {
                        const studentRef = doc(db, "students", student.id);
                        const studentDoc = await getDoc(studentRef);
                        const studentData = studentDoc.data();
                        
                        let enrollments = studentData.enrollments || [];
                        const existingIndex = enrollments.findIndex(e => e.classId === classId);
                        
                        if (existingIndex >= 0) {
                          enrollments[existingIndex] = {
                            ...enrollments[existingIndex],
                            fees: Number(feesAmount)
                          };
                        } else {
                          enrollments.push({
                            classId: classId,
                            fees: Number(feesAmount || 0),
                            paid: 0
                          });
                        }
                        
                        await updateDoc(studentRef, { enrollments });
                      })
                    );
                    
                    setShowFeesModal(false);
                    fetchData();
                  } catch (error) {
                    setError(`Error assigning fees: ${error.message}`);
                    setShowModal(true);
                  } finally {
                    setLoading(false);
                  }
                }}
              >
                Assign Fees
              </button>
            </div>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={isEditStudentFeesModalOpen}
        onRequestClose={() => setIsEditStudentFeesModalOpen(false)}
        contentLabel="Edit Student Fees"
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
      >
        <div>
          <p className="size5 caps">Update fees for <strong>{currentStudentFees?.firstName} {currentStudentFees?.lastName}</strong></p>
          <div className="modal-form">
            <label className='text-left'>Fees Amount</label>
            <input
              type="number"
              className="input-field"
              placeholder="Fees Amount"
              value={feesAmount}
              onChange={(e) => setFeesAmount(e.target.value)}
            />
            <div className="row align-center justify-center gap-15">
              <button 
                className="login-button"
                onClick={() => setIsEditStudentFeesModalOpen(false)}
              >
                Cancel
              </button>
              <button 
                className="login-button"
                onClick={async () => {
                  try {
                    setLoading(true);
                    const studentRef = doc(db, "students", currentStudentFees.id);
                    const studentDoc = await getDoc(studentRef);
                    const studentData = studentDoc.data();
                    
                    let enrollments = studentData.enrollments || [];
                    const existingIndex = enrollments.findIndex(e => e.classId === classId);
                    const currentFees = existingIndex >= 0 ? Number(enrollments[existingIndex].fees) || 0 : 0;
                    const currentPaid = existingIndex >= 0 ? Number(enrollments[existingIndex].paid) || 0 : 0;
                    const balanceAfterChange = Number(feesAmount) - currentPaid;
                    
                    if (existingIndex >= 0) {
                      enrollments[existingIndex] = {
                        ...enrollments[existingIndex],
                        fees: Number(feesAmount)
                      };
                    } else {
                      enrollments.push({
                        classId: classId,
                        fees: Number(feesAmount),
                        paid: 0
                      });
                    }
                    
                    await updateDoc(studentRef, { enrollments });

                    if (currentFees !== Number(feesAmount)) {
                      await addDoc(collection(db, "paymentHistory"), {
                        cashierName: `${userData.firstName} ${userData.lastName}`,
                        payerName: "System Update",
                        studentName: `${currentStudentFees.firstName} ${currentStudentFees.lastName}`,
                        className: classInfo.name,
                        term: classInfo.term,
                        session: classInfo.session,
                        previousFees: currentFees,
                        newFees: Number(feesAmount),
                        balance: balanceAfterChange,
                        timestamp: new Date(),
                        type: 'fee_update'
                      });
                    }
                    
                    setIsEditStudentFeesModalOpen(false);
                    fetchData();
                  } catch (error) {
                    setError(`Error updating student fees: ${error.message}`);
                    setShowModal(true);
                  } finally {
                    setLoading(false);
                  }
                }}
              >
                Update Fees
              </button>
            </div>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={isPayStudentFeesModalOpen}
        onRequestClose={() => setIsPayStudentFeesModalOpen(false)}
        contentLabel="Pay Student Fees"
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
      >
        <div>
          <p className="size5 caps">Payment for <strong>{currentStudentFees?.firstName} {currentStudentFees?.lastName}</strong></p>
          <div className="modal-form">
            <label className='text-left'>Amount</label>
            <input
              type="number"
              className="input-field"
              placeholder="Amount"
              value={paidAmount === 0 ? '' : paidAmount}
              onChange={(e) => setPaidAmount(e.target.value)}
            />
            <label className='text-left'>Payer's Name</label>
            <input
              type="text"
              className="input-field"
              placeholder="Payer's Name"
              value={payerName}
              onChange={(e) => setPayerName(e.target.value)}
            />
            <label className='text-left'>Payment For: &nbsp;</label>
            <div className="checkbox-group column gap-10">
              {paymentForOptions.map(option => (
                <label key={option}>
                  <input
                    type="checkbox"
                    className="input-checkbox gold-tick male-female-stat"
                    checked={paymentFor.includes(option)}
                    onChange={() => {
                      setPaymentFor(prev =>
                        prev.includes(option)
                          ? prev.filter(item => item !== option)
                          : [...prev, option]
                      );
                    }}
                  />
                  {option}
                </label>
              ))}
            </div>
            <label className='text-left'>Payment Method</label>
            <select
              className="input-field"
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value)}
            >
              {paymentMethodOptions.map(method => (
                <option key={method} value={method}>{method}</option>
              ))}
            </select>
            <div className="row align-center justify-center gap-15">
              <button 
                className="login-button"
                onClick={() => setIsPayStudentFeesModalOpen(false)}
              >
                Cancel
              </button>
              <button 
                className="login-button"
                onClick={async () => {
                  try {
                    setLoading(true);
                    const studentRef = doc(db, "students", currentStudentFees.id);
                    const studentDoc = await getDoc(studentRef);
                    const studentData = studentDoc.data();
                    
                    let enrollments = studentData.enrollments || [];
                    const existingIndex = enrollments.findIndex(e => e.classId === classId);
                    const currentPaid = existingIndex >= 0 ? Number(enrollments[existingIndex].paid) || 0 : 0;
                    const currentFees = existingIndex >= 0 ? Number(enrollments[existingIndex].fees) || 0 : 0;
                    const newPaidAmount = currentPaid + Number(paidAmount || 0);
                    const balanceAfterPayment = currentFees - newPaidAmount;
                    
                    if (existingIndex >= 0) {
                      enrollments[existingIndex] = {
                        ...enrollments[existingIndex],
                        paid: newPaidAmount
                      };
                    } else {
                      enrollments.push({
                        classId: classId,
                        fees: 0,
                        paid: newPaidAmount
                      });
                    }
                    
                    await updateDoc(studentRef, { enrollments });

                    if (Number(paidAmount) > 0) {
                      const paymentForData = paymentFor.length > 0 ? paymentFor : ['School fees'];
                      await addDoc(collection(db, "paymentHistory"), {
                        cashierName: `${userData.firstName} ${userData.lastName}`,
                        payerName: payerName || "Unknown",
                        admissionNumber: currentStudentFees.admissionNumber || "N/A",
                        studentName: `${currentStudentFees.firstName} ${currentStudentFees.lastName}`,
                        className: classInfo.name,
                        term: classInfo.term,
                        session: classInfo.session,
                        amountPaid: Number(paidAmount),
                        balance: balanceAfterPayment,
                        paymentFor: paymentForData,
                        paymentMethod: paymentMethod,
                        timestamp: new Date(),
                        type: 'payment',
                        studentStatus: currentStudentFees.status || "Regular",
                        discountAmount: currentStudentFees.discountAmount || 0,
                        effectiveBalanceAfterPayment: currentFees - newPaidAmount - (currentStudentFees.status === "Discount" ? Number(currentStudentFees.discountAmount || 0) : 0)
                      });
                    }
                    
                    setIsPayStudentFeesModalOpen(false);
                    setPayerName('');
                    setPaymentFor(['School fees']);
                    fetchData();
                  } catch (error) {
                    setError(`Error recording payment: ${error.message}`);
                    setShowModal(true);
                  } finally {
                    setLoading(false);
                  }
                }}
              >
                Record Payment
              </button>
            </div>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={showHistoryModal}
        onRequestClose={() => setShowHistoryModal(false)}
        contentLabel="Payment History"
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
      >
        <div>
          <p className="size5 caps">Payment history for <strong>{currentStudentFees?.firstName} {currentStudentFees?.lastName}</strong></p>
          <div className="modal-form">
            {fetchingHistory ? (
              <div className="column align-center justify-center">
                <p>Fetching . . .</p>
              </div>
            ) : paymentHistory.length === 0 ? (
              <p>No payment history available</p>
            ) : (
              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {paymentHistory.map(payment => (
                  <div key={payment.id} style={{border: 'none'}} className="info-item">
                    <div>
                      {payment.type === 'fee_update' ? (
                        <>
                          <span className="font-gold">FEES UPDATE</span><br />
                          <span>Previous: N {(payment.previousFees ?? 0).toLocaleString()}</span><br />
                          <span>New: N {(payment.newFees ?? 0).toLocaleString()}</span><br />
                          <span className='bold'>Updated by: {payment.cashierName}</span><br />
                        </>
                      ) : (
                        <>
                          <span className='bold'>Cashier: {payment.cashierName}</span><br />
                          <span>Payer: {payment.payerName}</span><br />
                          <span>Method: <strong>{payment.paymentMethod || 'Transfer'}</strong></span><br />
                          <span>Amount: N {payment.amountPaid.toLocaleString()}</span><br />
                          {payment.studentStatus === "Discount" || payment.studentStatus === "Scholarship" ? (
                            <>
                              <span>Balance: N {payment.effectiveBalanceAfterPayment?.toLocaleString() || '—'} ({payment.studentStatus.toLowerCase()})</span><br />                             
                            </>
                          ) : (
                            <>
                              <span>Balance: N {payment.balance?.toLocaleString() || '—'}</span><br />
                            </>
                          )}
                        </>
                      )}
                      <span className='row space-between align-center'>
                        <span className='size5'>{new Date(payment.timestamp.toDate()).toLocaleString()}</span>                        
                        <span className='row space-between align-center'>                          
                          {payment.type !== 'fee_update' && (
                            <div className="row gap-1">
                              <button 
                                className="list-btn"
                                onClick={() => generateReceiptPDF(
                                  payment, 
                                  userData.firstName, 
                                  userData.lastName,
                                  payment.admissionNumber || currentStudentFees?.admissionNumber || "N/A"
                                )}
                              >
                                <i className="fas fa-print"></i>
                              </button>

                              <button 
                                className="list-btn delete-btn"
                                onClick={() => {
                                  setPaymentToDelete(payment);
                                  setIsDeleteModalOpen(true);
                                }}
                              >
                                <i className="fas fa-trash-alt"></i>
                              </button>
                            </div>
                          )}
                        </span>
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className="row align-center justify-center gap-15">
              <button 
                className="login-button"
                onClick={() => setShowHistoryModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </Modal>
      <Modal
        isOpen={isDeleteModalOpen}
        onRequestClose={() => {
          setIsDeleteModalOpen(false);
          setPaymentToDelete(null);
        }}
        contentLabel="Confirm Delete Payment"
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
      >
        <div>
          <p className="size5 caps bold">Delete Payment Record?</p>
          <p style={{ margin: '15px 0', color: '#e74c3c', fontWeight: 'bold' }}>
            This action cannot be undone.
          </p>

          {paymentToDelete && (
            <div style={{ marginBottom: '20px', lineHeight: '1.5' }}>
              <div>Student: {paymentToDelete.studentName}</div>
              <div>Amount: ₦{paymentToDelete.amountPaid.toLocaleString()}</div>
              <div>Date: {new Date(paymentToDelete.timestamp.toDate()).toLocaleString()}</div>
              <div>Method: {paymentToDelete.paymentMethod}</div>
            </div>
          )}

          <div className="row align-center justify-center gap-15" style={{ marginTop: '25px' }}>
            <button 
              className="login-button"
              onClick={() => {
                setIsDeleteModalOpen(false);
                setPaymentToDelete(null);
              }}
            >
              Cancel
            </button>

            <button 
              className="login-button"
              style={{ background: '#e74c3c', borderColor: '#c0392b', color: 'white' }}
              onClick={async () => {
                if (!paymentToDelete) return;

                try {
                  setLoading(true);

                  // 1. Delete the payment history document
                  await deleteDoc(doc(db, "paymentHistory", paymentToDelete.id));

                  // 2. Reverse the payment in student's enrollment
                  const studentRef = doc(db, "students", currentStudentFees.id);
                  const studentSnap = await getDoc(studentRef);
                  
                  if (studentSnap.exists()) {
                    const data = studentSnap.data();
                    let enrollments = data.enrollments || [];
                    
                    const enrollmentIndex = enrollments.findIndex(e => e.classId === classId);
                    
                    if (enrollmentIndex !== -1) {
                      const currentPaid = Number(enrollments[enrollmentIndex].paid) || 0;
                      const newPaid = Math.max(0, currentPaid - Number(paymentToDelete.amountPaid || 0));

                      enrollments[enrollmentIndex] = {
                        ...enrollments[enrollmentIndex],
                        paid: newPaid
                      };

                      await updateDoc(studentRef, { enrollments });
                    }
                  }

                  // 3. Clean up & refresh
                  setIsDeleteModalOpen(false);
                  setPaymentToDelete(null);
                  setSuccessMessage("Payment record deleted and balance reversed.");
                  setShowModal(true);

                  // Refresh history and student list
                  fetchPaymentHistory(currentStudentFees);
                  fetchData();

                } catch (err) {
                  console.error(err);
                  setError(`Failed to delete payment: ${err.message}`);
                  setShowModal(true);
                } finally {
                  setLoading(false);
                }
              }}
            >
              Delete Payment
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Fees;